import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import { bool, func } from 'prop-types';
import {
  Modal,
  Button,
  Stack,
  ButtonGroup,
  Input,
  Loader,
  TextLink,
  Text,
} from '@chaser/components';
import usePush from '../../../../hooks/usePush';
import LabeledFieldWithError from '../../../../components/LabeledFieldWithError/index.jsx';
import { useHistory } from 'react-router';
import ConnectService from '../../../../services/connect-service';
import debounce from 'lodash/debounce';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import InfoLink from '../../../../components/InfoLink';
import { pendo } from '../../../../util/logging';

const CSVModal = ({ open, close, onClosed, sftpConfiguration, ShortCode }) => {
  const history = useHistory();
  const submittingRef = useRef(false);
  const { execute: eConnect } = usePush(
    ShortCode ? ConnectService.updateCSV : ConnectService.connectCSV,
    {
      successToast: 'Successfully updated connection details',
      errorToast: 'Failed to connect',
    },
  );

  const { execute: preview, loading, error, response } = usePush(ConnectService.previewCSV);

  const form = useFormik({
    initialValues: {
      host: sftpConfiguration?.host || '',
      port: sftpConfiguration?.post || 22,
      directory: sftpConfiguration?.directory || '',
      username: sftpConfiguration?.username || '',
      password: '',
    },
    validate: ({ host, port, username, password }) => {
      const errors = {};
      if (!host) errors.host = 'Please enter a host!';
      if (!username) errors.username = 'Please enter a user!';
      if (!password) errors.password = 'Please enter a password!';
      const portInt = parseInt(port);
      if (isNaN(portInt) || portInt < 0 || portInt > 65535)
        errors.port = 'Port must be a number between 0 and 65535!';

      return errors;
    },
    validateOnMount: true,
    onSubmit: async (configuration, { setSubmitting }) => {
      const [errPreview, resPreview] = await preview({
        sftpConfiguration: configuration,
        shortCode: ShortCode,
      });

      if (errPreview || !resPreview?.readyToConnect) {
        setSubmitting(false);
        return;
      }

      const [err, res] = await eConnect({
        sftpConfiguration: configuration,
        ShortCode,
      });

      setSubmitting(false);
      if (err) return;
      close();
      pendo.track('integration_modal_submitted', {
        integration: 'csv',
        isReconnect: !!ShortCode,
      });
      if (res.ShortCode) history.push(`/?organisation=${res.ShortCode}`);
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderPreview = useCallback(
    debounce(configuration => {
      if (submittingRef.current) return;
      preview({
        sftpConfiguration: configuration,
        shortCode: ShortCode,
      });
    }, 2000),
    [],
  );

  useOnUpdate(() => {
    if (!form.isValid) return;
    renderPreview(form.values);
  }, [form.values, form.isValid]);

  useLayoutEffect(() => {
    submittingRef.current = form.isSubmitting;
  }, [form.isSubmitting]);

  return (
    <Modal title="CSV Integration" open={open} toggle={close} onClosed={onClosed}>
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            <Stack gap="medium">
              <Stack>
                <Text>
                  Import CSV files via FTP server. {sftpConfiguration ? 'Update' : 'Add'} your FTP
                  server credentials.{' '}
                  <InfoLink
                    to="https://help.chaserhq.com/chase-import"
                    label="Info about CSV Integration"
                  />
                </Text>
              </Stack>
              <Stack>
                <LabeledFieldWithError
                  name="host"
                  as={Input}
                  label="Host"
                  width="full"
                  placeholder="sftp.example.com"
                />
                <LabeledFieldWithError
                  name="port"
                  type="number"
                  min="0"
                  max="65535"
                  as={Input}
                  label="Port"
                  width="full"
                  placeholder="22"
                />
                <LabeledFieldWithError
                  name="directory"
                  as={Input}
                  label="Directory (optional)"
                  width="full"
                />
                <LabeledFieldWithError
                  name="username"
                  as={Input}
                  label="Username"
                  width="full"
                  autocomplete="off"
                />
                <LabeledFieldWithError
                  name="password"
                  type="password"
                  autocomplete="off"
                  as={Input}
                  label="Password"
                  width="full"
                />
                {loading && !form.isSubmitting && <Loader />}
                {error && (
                  <Text color="Red">
                    {error}
                    <br />
                    We couldn’t connect to your SFTP Server.
                    <br /> Please check the information is correct, and if the problem persists{' '}
                    <TextLink as="a" monochrome href="mailto:support@chaserhq.com">
                      contact us
                    </TextLink>
                    .
                  </Text>
                )}
                {!loading && !error && response && (
                  <Stack gap="small">
                    {response.fileNames && response.fileNames.length > 0
                      ? 'Found following files to sync: ' + response.fileNames.join(', ') + '.'
                      : 'We successfully connected to your SFTP Server, but no files were found to sync. Please proceed if you plan to upload files later.'}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button disabled={!form.isValid} loading={form.isSubmitting} type="submit" data-trackingid={`integration-csv-${ShortCode ? 'update' : 'connect'}`}>
                {ShortCode ? 'Update' : 'Connect'}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

CSVModal.propTypes = {
  open: bool.isRequired,
  close: func.isRequired,
  onClosed: func,
};

export default CSVModal;
